<template>
  <v-dialog
    v-model="visibility"
    role="dialog"
    :aria-modal="true"
    :aria-labelledby="`post-dialog-title-${post.slug || 'unknown'}`"
    :fullscreen="$vuetify.breakpoint.mdAndDown ? true : false"
    :width="$vuetify.breakpoint.mdAndDown ? 'auto' : '800'"
    transition="dialog-bottom-transition"
    class="post-dialog"
    @keydown.esc="closeDialog"
    @input="handleDialogToggle"
  >
    <v-card>
      <v-card-title
        :id="`post-dialog-title-${post.slug || 'unknown'}`"
        class="h6 font-weight-regular post-dialog-title mt-4"
        color="accent"
      >
        <span
          v-html="post.title"
        />

        <v-spacer />
        <v-btn
          :aria-label="`Open original post in new tab - ${post.date}`"
          :title="$t('components.common.openExternal')"
          class="post-dialog-date subheading text-subtitle-1 font-weight-bold link-button"
          @click="toLink(link)"
        >
          {{ post.date }}
        </v-btn>
      </v-card-title>

      <v-img
        v-if="post.full_picture"
        :src="post.full_picture"
        :alt="post.title || 'Post image preview'"
      />
      <v-card-text
        class="post-dialog-text"
        v-html="html"
      />
      <v-divider aria-hidden="true" />
      <v-card-actions>
        <v-btn
          ref="closeButton"
          aria-label="Close dialog"
          color="accent"
          text
          @click="closeDialog"
          @keydown.tab="handleTabNavigation"
        >
          <v-icon aria-hidden="true">
            fal fa-times
          </v-icon>
          <span>Close</span>
        </v-btn>

        <v-btn
          ref="copyButton"
          aria-label="Copy post link"
          color="accent"
          text
          @click="copyToClipboard(link)"
          @keydown.tab="handleTabNavigation"
        >
          <v-icon aria-hidden="true">
            fad fa-clipboard
          </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import { toPostLink as asPostLink } from "@/utils/post.utils";
import { copyToClipboard } from "@/utils/text.utils";
import types from "@/constants/types";
import actions from '@/store/actions';
import AccessibilityMixin from "@/mixins/accessibility.mixin.js";

export default {
  name: "PostDialog",
  mixins: [AccessibilityMixin],
  props: {
    post: {
        type: Object,
        default: () => {},
    },
  },
  data() {
    return {
        visibility: false,
        previousActiveElement: null,
        focusableElements: []
    }
  },
  computed: {
    isVisible() {
      return (this.post.slug);
    },
    link() {
      return asPostLink(this.post.slug);
    },
    html() {
        return ((this.post || "").htmlText || "")
        .replace(/(<a)/g,'<a class="post-dialog-text-link"')
        .replace(/(<hr)/g,'<hr class="post-dialog-text-hr"')
        .replace(/(<p)/g,'<p class="post-dialog-text-paragraph"')
        .replace(/(<h)([1-6])/g,'<h$2 class="h$2 my-2"')
        .replace(/(class="wp-image)/g,'class="post-dialog-text-image wp-image')
    }
  },
  watch: {
    isVisible(newValue) {
      this.visibility = this.isVisible;
    },
    visibility(newValue) {
        this.updateDialog(newValue);
    }
  },
  methods: {
    ...mapActions({
      updateDialog: types.UPDATE_COMPONENT_VISIBILITY
    }),

    toLink(link) {
      window.open(link);
    },

    copyToClipboard(link) {
        copyToClipboard(link).then(() => {
            actions.updateNotifications([{
                message: this.$t("components.clipboard"),
                color: 'success',
                icon: 'clipboard',
                iconColor: 'white',
            }]);
        })
    },

    /**
     * Close dialog with accessibility features
     */
    closeDialog() {
      this.updateDialog(false);
    },

    /**
     * Handle dialog toggle for focus management
     * @param {boolean} isOpen - Whether dialog is opening or closing
     */
    handleDialogToggle(isOpen) {
      if (isOpen) {
        // Store the element that had focus before opening
        this.previousActiveElement = document.activeElement;

        // Set up focus trap and focus first element
        this.$nextTick(() => {
          this.setupFocusTrap();
          this.focusFirstElement();

          // Announce dialog opening
          this.announceToScreenReader(
            `Dialog opened: ${this.post.title || 'Post details'}`,
            'polite'
          );
        });
      } else {
        // Release focus trap and restore previous focus
        this.releaseFocusTrap();
        this.restorePreviousFocus();

        // Announce dialog closing
        this.announceToScreenReader('Dialog closed', 'polite');
      }
    },

    /**
     * Set up focus trap within the dialog
     */
    setupFocusTrap() {
      this.focusableElements = this.getFocusableElementsInDialog();

      if (this.focusableElements.length > 0) {
        this.trapFocus(this.$el.querySelector('.v-card'));
      }
    },

    /**
     * Get focusable elements within the dialog
     * @returns {Array<Element>}
     */
    getFocusableElementsInDialog() {
      if (!this.$el) return [];

      const focusableSelectors = [
        'button:not([disabled])',
        '[role="button"]:not([disabled])',
        'a[href]:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');

      return Array.from(this.$el.querySelectorAll(focusableSelectors))
        .filter(el => el.offsetParent !== null);
    },

    /**
     * Focus the first element in the dialog
     */
    focusFirstElement() {
      if (this.$refs.closeButton && this.$refs.closeButton.$el) {
        this.setAccessibleFocus(this.$refs.closeButton.$el);
      }
    },

    /**
     * Restore focus to the element that was focused before opening
     */
    restorePreviousFocus() {
      if (this.previousActiveElement && this.previousActiveElement.focus) {
        this.previousActiveElement.focus();
        this.previousActiveElement = null;
      }
    },

    /**
     * Handle tab navigation within the dialog for focus trapping
     * @param {KeyboardEvent} event
     */
    handleTabNavigation(event) {
      const focusableElements = this.getFocusableElementsInDialog();

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentElement = event.target;

      if (event.shiftKey) {
        // Shift + Tab - moving backwards
        if (currentElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab - moving forwards
        if (currentElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },

    /**
     * Handle escape key press
     */
    handleEscape() {
      if (this.visibility) {
        this.closeDialog();
        return true; // Stop propagation
      }
      return false;
    }
  }
};
</script>

<style lang="scss">
@import "~/_variables.scss";

.post-dialog {
  &-title {
      background: $accent;
  }

    &-date {
        text-decoration: none!important;
        color: $primary;
        background: transparent;
        border: none;
        font: inherit;
        cursor: pointer;
        padding: 0;

        &:focus {
          outline: 2px solid $accent;
          outline-offset: 2px;
        }
    }

  &-text {
      margin: 0.8rem 0;

      &-link {
        color: $accent!important;
        text-decoration: overline!important;
      }

      &-hr {
        margin: 0.8rem 0!important;
      }

      &-image {
        margin: auto!important;
        width: 100%;
        height: 100%;
        border: 1px solid gray;
        padding: 0.5rem;
      }

    &-paragraph {
      word-wrap: break-word;
      width: 96%;
      white-space: pre-wrap;
      margin: 1rem;
    }

    .wp-block-gallery {
      list-style: none;
      padding-left: 0;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      position: relative;
    }

    .blocks-gallery-item {
      width: calc((100% - 16px) / 2.1);
      flex: 1 1 auto;
      margin: 0.5rem;
      height: 17rem;

      figure {
        margin: 0;
        height: 100%;
        width: 100%;

        > img {
          width: 100%;
          height: 100%;
          max-width: -webkit-fill-available;
          max-height: -webkit-fill-available;
        }
      }
    }
  }
}

@media all and (max-width: 700px) and (min-width: 451px) {
  .post-dialog-text .blocks-gallery-item {
    height: 12rem;
  }
}

@media all and (max-width: 450px) and (min-width: 100px) {
  .post-dialog-text .blocks-gallery-item {
    height: 9rem;
  }
}
</style>
